
import {
  type BookingRange,
  type BookingWindow,
  bookingWindow,
  isValidBookingRange,
  isWithinWindow,
  toInstant,
  SLOT_MINUTES,
} from '@shared/booking';
import { slotStatus, slotRange, rangeCoversSlot } from './week-grid.ts';
import {
  cellsToRanges,
  parseCellKey,
  type CellKey,
  type CellState,
  type InstantRange,
} from '@client/shared/calendar/slot-grid';
import { localRangeToInstants } from '@client/shared/lib/wall-clock-intake.ts';

export function toRange(r: InstantRange): BookingRange {
  return { start: toInstant(r.start), end: toInstant(r.end) };
}

export function cellStatus(
  slotStartInstant: Date,
  window: BookingWindow,
  bookings: readonly BookingRange[],
  unavailability: readonly BookingRange[],
): Exclude<CellState, 'selected'> {
  const cell = slotRange(slotStartInstant);
  if (!isWithinWindow(cell, window)) return 'disabled';
  const status = slotStatus(slotStartInstant, bookings, unavailability);
  if (status === 'unavailable') return 'unavailable';
  if (status === 'booked') return 'booked';
  return 'free';
}

export function cellStartInstant(date: string, slot: number, zone: string): Date {
  const { start } = localRangeToInstants(date, slot * SLOT_MINUTES, (slot + 1) * SLOT_MINUTES, zone);
  return new Date(start);
}

export function windowFor(now: Date, horizonDays: number): BookingWindow {
  return bookingWindow(now, Math.max(1, horizonDays));
}

export interface GridSpan {
  readonly dayIndex: number;
  readonly startSlot: number;
  readonly endSlot: number;
}

export function instantRangeToGridSpans(
  start: Date,
  end: Date,
  days: readonly string[],
  zone: string,
  visible: { readonly startSlot: number; readonly endSlot: number },
): GridSpan[] {
  const spans: GridSpan[] = [];
  const startMs = start.getTime();
  const endMs = end.getTime();
  if (!(endMs > startMs)) return spans;

  days.forEach((date, dayIndex) => {
    let runStart: number | null = null;
    const flush = (endSlotExclusive: number) => {
      if (runStart === null) return;
      spans.push({ dayIndex, startSlot: runStart, endSlot: endSlotExclusive });
      runStart = null;
    };
    for (let slot = visible.startSlot; slot < visible.endSlot; slot++) {
      const slotStart = cellStartInstant(date, slot, zone).getTime();
      const slotEnd = cellStartInstant(date, slot + 1, zone).getTime();
      const covered = startMs < slotEnd && endMs > slotStart;
      if (covered) {
        if (runStart === null) runStart = slot;
      } else {
        flush(slot);
      }
    }
    flush(visible.endSlot);
  });

  return spans;
}

export type SelectionResult =
  | { readonly kind: 'empty' }
  | { readonly kind: 'ok'; readonly range: BookingRange }
  | { readonly kind: 'not_contiguous' }
  | { readonly kind: 'invalid' };

export function selectionToBooking(
  painted: ReadonlySet<CellKey>,
  days: readonly string[],
  zone: string,
): SelectionResult {
  if (painted.size === 0) return { kind: 'empty' };
  const dayIdxs = new Set([...painted].map((k) => parseCellKey(k).day));
  if (dayIdxs.size > 1) return { kind: 'not_contiguous' };

  const ranges = cellsToRanges(painted, days, zone);
  if (ranges.length !== 1) return { kind: 'not_contiguous' };

  const range = toRange(ranges[0]!);
  if (!isValidBookingRange(range)) return { kind: 'invalid' };
  return { kind: 'ok', range };
}

export function rangeConflicts(
  range: BookingRange,
  bookings: readonly BookingRange[],
  unavailability: readonly BookingRange[],
): boolean {
  const overlaps = (b: BookingRange) =>
    range.start.getTime() < b.end.getTime() && range.end.getTime() > b.start.getTime();
  return bookings.some(overlaps) || unavailability.some(overlaps);
}

export { rangeCoversSlot };
